import InspirationCard from "@/components/InspirationCard";
import ApiService from "@/libs/ApiServices";
import {IJournal, IJournalCategory} from "@/types/journal.types";
import {Button, Skeleton, Tabs} from "antd";
import {GetServerSideProps} from "next";
import Link from "next/link";
import React, {useState} from "react";
import {toast} from "react-hot-toast";
import {UseInfiniteQueryResult, useInfiniteQuery} from "react-query";

let limit_journal = 10;

interface Props {
  blog_highlight: IJournal[] | [];
  blog_category: IJournalCategory[] | [];
}

const Inspiration: React.FC<Props> = ({blog_category, blog_highlight}) => {
  const [activeTab, setActiveTab] = useState(blog_category[0].slug);
  const [selectedCategory, setSelectedCategory] = useState(blog_category[0].slug);

  // react query
  const {data, isLoading, hasNextPage, fetchNextPage}: UseInfiniteQueryResult<IJournal[] | null> = useInfiniteQuery({
    queryKey: ["blog", selectedCategory],
    queryFn: ({pageParam = 1}) => getJournal(pageParam),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.length === limit_journal ? allPages.length + 1 : undefined;
    },
  });

  const getJournal = async (page: number) => {
    try {
      const response = await ApiService.get(`/blog?limit=${limit_journal}&page=${page}&slug=${selectedCategory}`);

      if (response.data.status !== 200) {
        throw new Error(response.data.message);
      }
      return response.data.data;
    } catch (error) {
      toast.error("Failed to get journal");
    }
  };

  const changeTab = async (activeKey: string) => {
    setSelectedCategory(activeKey);
    setActiveTab(activeKey);
  };

  return (
    <section className="mt-4 mb-32 md:mt-10 wrapper">
      <h1 className="text-center uppercase">INSPIRATION</h1>
      <section className="grid grid-cols-2 gap-4 mt-10 lg:gap-14 wrapper-sm">
        {blog_highlight.map((blog, i) => (
          <div key={i + 58} className="group">
            <Link href={"/inspiration/" + blog.slug}>
              <img className="object-cover w-full aspect-square " src={blog.thumbnail} alt={blog.title} />
            </Link>
            <div className="mt-4 text-sm">
              <span className="block text-center text-gray-base md:text-left">{blog.category.name}</span>
              <Link href={"/inspiration/" + blog.slug}>
                <p className="mt-2 text-center md:text-left line-clamp-3 group-hover:font-medium">{blog.title}</p>
              </Link>
              <div className="flex justify-center md:justify-start">
                <Link
                  className="text-center underline group-hover:no-underline text-blue-base md:text-left"
                  href={"/inspiration/" + blog.slug}
                >
                  Read More
                </Link>
              </div>
            </div>
          </div>
        ))}
      </section>

      <Tabs className="mt-10 lg:mt-12 journal-tabs-custom" activeKey={activeTab} onChange={changeTab}>
        {blog_category?.map((category) => (
          <Tabs.TabPane tab={category.name} key={category.slug}>
            {!isLoading && !data?.pages[0]?.length && (
              <div className="flex justify-center min-h-[20vh] items-center">
                <span className="text-sm">No Blog Available</span>
              </div>
            )}
            {isLoading ? (
              <section className="grid grid-cols-2 gap-4 mt-10 md:mt-14 lg:gap-x-11 gap-y-10 lg:gap-y-16 md:grid-cols-3">
                {Array(3)
                  .fill("")
                  .map((_, i) => (
                    <div key={i} className="flex flex-col justify-center space-y-2">
                      <div className="h-56">
                        <Skeleton.Input active className="w-full h-full" />
                      </div>
                      <Skeleton.Input active className="w-10 h-5" />
                      <Skeleton.Input active className="w-full h-5" />
                      <Skeleton.Input active className="w-full h-5" />
                      <Skeleton.Input active className="w-10 h-5" />
                    </div>
                  ))}
              </section>
            ) : (
              <section className="grid grid-cols-2 gap-4 mt-10 md:mt-14 lg:gap-x-11 gap-y-10 lg:gap-y-16 md:grid-cols-3">
                {data?.pages.map((data) => data?.map((journal) => <InspirationCard key={journal.id} data={journal} />))}
              </section>
            )}
          </Tabs.TabPane>
        ))}
      </Tabs>

      {hasNextPage ? (
        <section className="flex justify-center mt-16">
          <Button
            loading={isLoading}
            onClick={() => fetchNextPage()}
            type="ghost"
            className="px-10 rounded-full text-blue-base border-blue-base"
          >
            Load More
          </Button>
        </section>
      ) : null}
    </section>
  );
};

export default Inspiration;

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const header_response = {
      headers: {
        organization: process.env.NEXT_PUBLIC_ORGANIZATION_ID,
      },
    };

    const response_blog_highlight = await ApiService.get(`/blog/banner`, header_response);

    if (response_blog_highlight.data.status !== 200) {
      throw new Error(response_blog_highlight.data.message);
    }

    const response_blog_category = await ApiService.get(`blog-category`, header_response);
    if (response_blog_category.data.status !== 200) {
      throw new Error(response_blog_category.data.message);
    }

    return {
      props: {
        blog_highlight: response_blog_highlight.data.data,
        blog_category: response_blog_category.data.data,
      },
    };
  } catch (error) {
    return {notFound: true};
  }
};
